import { memo, useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  IInspectionMenuAction,
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiPlaceholderArea,
  MiTab,
} from "@miplatform/components";
import { CALENDAR_TYPES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  ButtonVariants,
  IInspectionAttempt,
  IVerticalTabProps,
  InspectionActionTypes,
} from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiFormatListBulleted, mdiPlus } from "@mdi/js";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { InspectionBoard } from "./components/InspectionBoard";
import { InspectionCancelModal } from "./components/InspectionCancelModal/InspectionCancelModal";
import { InspectionDrawer } from "./components/InspectionDrawer/InspectionDrawer";
import { InspectionSearch } from "./components/InspectionDrawer/InspectionSearch/InspectionSearch";
import {
  IInspectionForm,
  getInspectionFormDefaultValues,
  inspectionFormSchema,
} from "./components/InspectionDrawer/inspectionFormSchemas";
import { InspectionList } from "./components/InspectionList";
import {
  InspectionsProvider,
  useInspectionsContext,
} from "./InspectionsProvider";

export const InspectionsContent = () => {
  const {
    dateRange,
    inspectionTemplates,
    getInspectionTemplates,
    isLoading,
    calendarSet,
    activeCalendar,
    setSelectedInspectionAttemptListItem,
    setShowHighlight,
    getJumpRange,
    jumpToInspection,
    templateFilterOptions,
    inspectionDrawerOpen,
    setIsConfirmationModalOpen,
    setIsCancelModalOpen,
    setDrawerType,
    drawerType,
    setInspectionDrawerOpen,
    refreshInspections,
    cancelInspectionAttempt,
    selectedInspectionAttempt,
    isCancelModalOpen,
  } = useInspectionsContext();

  const { params } = useRouter();
  const { calendarSetId } = params;

  // Setting was removed
  // const showLeftFilterPanel = !!activeCalendar?.settings.showLeftFilterPanel;
  const showLeftFilterPanel = true;

  const { classes, theme } = useStyles({ showLeftFilterPanel });
  const { navigate } = useRouter();

  const formControl = useForm<IInspectionForm>({
    defaultValues: getInspectionFormDefaultValues({ type: drawerType }),
    resolver: zodResolver(inspectionFormSchema),
    mode: "onChange",
  });
  const { reset } = formControl;
  const { isDirty } = formControl.formState;

  const [templateFilter, setTemplateFilter] =
    useState<IVerticalTabProps<any> | null>(null);

  useEffect(() => {
    getInspectionTemplates();
  }, []);

  useEffect(() => {
    setTemplateFilter(null);
  }, [activeCalendar]);

  const tabs = useMemo(() => {
    if (!calendarSet?.calendarConfigs) return [];
    return calendarSet.calendarConfigs?.map((calendar) => ({
      label: calendar.name,
      value: calendar.id,
    }));
  }, [calendarSet]);

  const handleSearchSelect = (inspectionAttempt: IInspectionAttempt) => {
    setSelectedInspectionAttemptListItem(inspectionAttempt);
    setShowHighlight(true);

    if (inspectionAttempt.approvedStartDate) {
      const range = getJumpRange(
        inspectionAttempt.approvedStartDate,
        dateRange
      );
      if (range) jumpToInspection(range);
    }
  };

  const handleInspectionClick = ({
    inspectionAttempt,
    actionType = null,
  }: IInspectionMenuAction) => {
    if (inspectionDrawerOpen && isDirty) {
      setIsConfirmationModalOpen(true);
      return;
    }

    setSelectedInspectionAttemptListItem(inspectionAttempt);

    if (actionType === InspectionActionTypes.Cancel) {
      setIsCancelModalOpen(true);
      return;
    }

    setDrawerType(actionType);
    setInspectionDrawerOpen(true);
    setShowHighlight(false);
  };

  const renderInspectionBoard = () => {
    const isKanban = activeCalendar?.calendarType === CALENDAR_TYPES.KANBAN;
    const isList = activeCalendar?.calendarType === CALENDAR_TYPES.HOLDS_LIST;

    if (!calendarSetId && !isLoading)
      return (
        <MiPlaceholderArea
          text="No calendar set found"
          helperText="Please choose a calendar set from navigation."
        />
      );

    if (!isKanban && !isList && !isLoading) {
      return (
        <MiPlaceholderArea
          text="Unsupported calendar type."
          helperText="Please choose a different calendar."
        />
      );
    }

    if (isList) {
      return (
        <InspectionList
          loading={isLoading}
          calendarName={activeCalendar?.name}
          onInspectionClick={handleInspectionClick}
        />
      );
    }

    return (
      <InspectionBoard
        loading={isLoading}
        templateFilter={templateFilter}
        leftFilterPanelEnabled={showLeftFilterPanel}
        onInspectionClick={handleInspectionClick}
      />
    );
  };

  const handleCalendarTabChange = (calendarId: string) => {
    navigate(`/inspections/${calendarSet?.id}/${calendarId}`);
  };

  const handleFilterTabChange = (tab: IVerticalTabProps<FieldValues>) => {
    setTemplateFilter(tab);
  };

  const handleDrawerClose = (refresh = false) => {
    setInspectionDrawerOpen(false);
    setIsConfirmationModalOpen(false);
    reset(getInspectionFormDefaultValues({ type: null }));
    if (refresh) {
      refreshInspections();
    }
  };

  const handleCancelInspection = async (data: { reason: string }) => {
    if (!selectedInspectionAttempt) return;
    await cancelInspectionAttempt({
      id: selectedInspectionAttempt.id,
      reason: data.reason,
    });
    setIsCancelModalOpen(false);
    refreshInspections();
  };

  return (
    <>
      <MiBuildPageHeader
        title="Inspections"
        loading={isLoading}
        titleIcon={
          <MiIcon
            path={mdiFormatListBulleted}
            size={1}
            color={theme.palette.primary.main}
          />
        }
        actionContainerStyle={{ flex: 1, flexDirection: "row" }}
        actionComponent={
          <Box className={classes.actionContainer}>
            <InspectionSearch
              onSelect={handleSearchSelect}
              templates={inspectionTemplates}
            />
            <MiButton
              title="Create New"
              variant={ButtonVariants.Contained}
              startIcon={{
                path: mdiPlus,
              }}
              onClick={() => navigate("/inspections/create")}
            />
          </Box>
        }
      />
      {calendarSet && calendarSet.calendarConfigs?.length > 1 && (
        <MiTab
          tabs={tabs}
          currentTab={activeCalendar?.id}
          onTabChange={handleCalendarTabChange}
        />
      )}
      {showLeftFilterPanel ? (
        <VerticalTabsLayout
          tabs={[
            { label: "All Inspections", value: null },
            ...templateFilterOptions,
          ]}
          useLegacyStyles
          hideStatusIcons
          contentStyles={{
            padding: 0,
          }}
          onTabChange={handleFilterTabChange}
          hideNavigationButtons
        >
          <MiBox className={classes.inspectionBoardContainer}>
            {renderInspectionBoard()}
          </MiBox>
        </VerticalTabsLayout>
      ) : (
        <MiBox className={classes.inspectionBoardContainer}>
          {renderInspectionBoard()}
        </MiBox>
      )}
      <InspectionDrawer
        open={inspectionDrawerOpen}
        onClose={handleDrawerClose}
        formControl={formControl}
      />
      <InspectionCancelModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onCancel={handleCancelInspection}
      />
    </>
  );
};

export const Inspections = memo(() => {
  return (
    <InspectionsProvider>
      <InspectionsContent />
    </InspectionsProvider>
  );
});

const useStyles = makeStyles<{ showLeftFilterPanel: boolean }>()(
  (theme, { showLeftFilterPanel }) => ({
    inspectionBoardContainer: {
      padding: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      position: "relative",
      margin: showLeftFilterPanel ? 0 : theme.spacing(2),
    },
    actionContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: theme.spacing(2),
    },
  })
);
